import axios from "axios";

// --- Settings ---

export const getExpirationSettings = async () => {
  try {
    const res: any = await axios.get("/quote-expiration/settings");
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const getGlobalExpirationSettings = async () => {
  try {
    const res: any = await axios.get("/quote-expiration/settings/global");
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const updateGlobalExpirationSettings = async (data: any) => {
  try {
    const res: any = await axios.put("/quote-expiration/settings/global", data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const setOrgExpirationOverrides = async (companyId: string, data: any) => {
  try {
    const res: any = await axios.put("/quote-expiration/settings/company/" + companyId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const getOrgExpirationOverrides = async (companyId: string) => {
  try {
    const res: any = await axios.get("/quote-expiration/settings/company/" + companyId);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const listClientExpirationOverrides = async () => {
  try {
    const res: any = await axios.get("/quote-expiration/settings/clients");
    return res?.data ? res.data : { clients: [] };
  } catch (e: any) {
    return { clients: [] };
  }
};

export const getClientExpirationOverrides = async (clientId: string) => {
  try {
    const res: any = await axios.get("/quote-expiration/settings/client/" + clientId);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const setClientExpirationOverrides = async (clientId: string, data: any) => {
  try {
    const res: any = await axios.put("/quote-expiration/settings/client/" + clientId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const deleteClientExpirationOverrides = async (clientId: string) => {
  try {
    const res: any = await axios.delete("/quote-expiration/settings/client/" + clientId);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const getClientNotificationPrefs = async (clientId: string) => {
  try {
    const res: any = await axios.get("/quote-expiration/notifications/client/" + clientId);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const setClientNotificationPrefs = async (clientId: string, data: any) => {
  try {
    const res: any = await axios.put("/quote-expiration/notifications/client/" + clientId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const runExpirationJob = async () => {
  try {
    const res: any = await axios.post("/quote-expiration/run-expiration");
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const sendExpirationDigest = async () => {
  try {
    const res: any = await axios.post("/quote-expiration/send-digest");
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const populateRetroactive = async (mode?: string, entity?: string, maxAgeDays?: number) => {
  try {
    let url = "/quote-expiration/populate-retroactive";
    const params: string[] = [];
    if (mode) params.push("mode=" + mode);
    if (entity) params.push("entity=" + entity);
    if (maxAgeDays) params.push("maxAgeDays=" + maxAgeDays);
    if (params.length > 0) url += "?" + params.join("&");
    const res: any = await axios.post(url);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const closeLegacyRecords = async (olderThanDays?: number) => {
  try {
    let url = "/quote-expiration/close-legacy";
    if (olderThanDays) url += "?olderThanDays=" + olderThanDays;
    const res: any = await axios.post(url);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const getAdminStats = async () => {
  try {
    const res: any = await axios.get("/quote-expiration/admin/stats");
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const getPipelineMetrics = async () => {
  try {
    const res: any = await axios.get("/quote-expiration/pipeline-metrics");
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

// --- Expiring items ---

export const getExpiringItems = async () => {
  try {
    const res: any = await axios.get("/quote-expiration/expiring");
    return res?.data ? res.data : { quotes: [], rfqs: [] };
  } catch (e: any) {
    return { quotes: [], rfqs: [] };
  }
};

// --- Decline ---

export const declineQuote = async (quoteId: string, data: { category: string | null; notes?: string; competitorName?: string }) => {
  try {
    const res: any = await axios.post("/quote-expiration/decline/" + quoteId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const declineRfq = async (rfqId: string, data: { category: string | null; notes?: string; competitorName?: string }) => {
  try {
    const res: any = await axios.post("/quote-expiration/decline-rfq/" + rfqId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const batchDecline = async (data: { quoteIds?: string[]; rfqIds?: string[]; category: string | null; notes?: string; competitorName?: string }) => {
  try {
    const res: any = await axios.post("/quote-expiration/batch-decline", data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const getQuoteDeclineReasons = async (quoteId: string) => {
  try {
    const res: any = await axios.get("/quote-expiration/decline-reasons/quote/" + quoteId);
    return res?.data ? res.data : [];
  } catch (e: any) {
    return [];
  }
};

export const getRfqDeclineReasons = async (rfqId: string) => {
  try {
    const res: any = await axios.get("/quote-expiration/decline-reasons/rfq/" + rfqId);
    return res?.data ? res.data : [];
  } catch (e: any) {
    return [];
  }
};

// --- RFQ Extension + Revival ---

export const extendRfq = async (rfqId: string) => {
  try {
    const res: any = await axios.post("/quote-expiration/extend-rfq/" + rfqId);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const reviveRfq = async (rfqId: string) => {
  try {
    const res: any = await axios.post("/quote-expiration/revive-rfq/" + rfqId);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

// --- Quote Regeneration ---

export const regenerateQuote = async (quoteId: string) => {
  try {
    const res: any = await axios.post("/quote-expiration/regenerate/" + quoteId);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const batchRegenerate = async (quoteIds: string[]) => {
  try {
    const res: any = await axios.post("/quote-expiration/batch-regenerate", { quoteIds });
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

// --- Quote Minor Version Edit ---

export const editQuoteVersion = async (quoteId: string, data: any) => {
  try {
    const res: any = await axios.post("/quote-expiration/edit/" + quoteId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

// --- Version History ---

export const getVersionHistory = async (quoteRequestId: string) => {
  try {
    const res: any = await axios.get("/quote-expiration/version-history/" + quoteRequestId);
    return res?.data ? res.data : [];
  } catch (e: any) {
    return [];
  }
};

// --- Price Lock + Deposits ---

export const lockPrice = async (quoteId: string, data?: { createStripeIntent?: boolean; lockDays?: number; depositPercent?: number }) => {
  try {
    const res: any = await axios.post("/quote-expiration/lock-price/" + quoteId, data || {});
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const recordDeposit = async (quoteId: string, data: { method: string; amount?: number; referenceId?: string; note?: string; paidAt?: string }) => {
  try {
    const res: any = await axios.post("/quote-expiration/record-deposit/" + quoteId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const voidLock = async (quoteId: string) => {
  try {
    const res: any = await axios.post("/quote-expiration/void-lock/" + quoteId);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const applyDepositCredit = async (quoteId: string, invoiceId: string) => {
  try {
    const res: any = await axios.post("/quote-expiration/apply-deposit-credit/" + quoteId, { invoiceId });
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const refundDeposit = async (quoteId: string, data: { note: string; refundMethod?: string }) => {
  try {
    const res: any = await axios.post("/quote-expiration/refund-deposit/" + quoteId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const getOutstandingDeposits = async (clientId?: string) => {
  try {
    const params: any = {};
    if (clientId) params.clientId = clientId;
    const res: any = await axios.get('/quote-expiration/outstanding-deposits', { params });
    return res?.data ? res.data : [];
  } catch (e: any) {
    return [];
  }
};

export const getActivePriceLocks = async () => {
  try {
    const res: any = await axios.get("/quote-expiration/active-locks");
    return res?.data ? res.data : { summary: { totalLocks: 0, pendingDeposit: 0, secured: 0, totalLockedValue: 0, totalDepositValue: 0 }, locks: [] };
  } catch (e: any) {
    return { summary: { totalLocks: 0, pendingDeposit: 0, secured: 0, totalLockedValue: 0, totalDepositValue: 0 }, locks: [] };
  }
};

export const sendScheduledDigests = async () => {
  try {
    const res: any = await axios.post("/quote-expiration/send-scheduled-digests");
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const createDepositIntent = async (quoteId: string, data: { paymentMethod: 'card' | 'ach'; stripePaymentMethodId?: string }) => {
  try {
    const res: any = await axios.post("/quote-expiration/create-deposit-intent/" + quoteId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const getNotificationPrefs = async (companyId: string) => {
  try {
    const res: any = await axios.get("/quote-expiration/notifications/" + companyId);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};

export const setNotificationPrefs = async (companyId: string, data: any) => {
  try {
    const res: any = await axios.put("/quote-expiration/notifications/" + companyId, data);
    return res?.data ? res.data : {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : e;
  }
};
